import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Own-comment id lookups.
 *
 * Public comment feeds deliberately do not expose the raw author `user_id`
 * column to anon or authenticated readers. These server functions resolve
 * "which of these comments are mine" server-side, scoped strictly to the
 * verified caller, so the UI can still render owner-only controls.
 */

export const mySongCommentIds = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z
      .object({
        releaseSlug: z.string().min(1).max(200),
        songSlug: z.string().min(1).max(200),
      })
      .parse(raw),
  )
  .handler(async ({ data, context }): Promise<string[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("song_comments")
      .select("id")
      .eq("user_id", context.userId)
      .eq("release_slug", data.releaseSlug)
      .eq("song_slug", data.songSlug)
      .limit(500);
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r) => r.id as string);
  });

export const myContentCommentIds = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z
      .object({
        contentKind: z.string().min(1).max(60),
        contentRef: z.string().min(1).max(200),
      })
      .parse(raw),
  )
  .handler(async ({ data, context }): Promise<string[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("content_comments")
      .select("id")
      .eq("user_id", context.userId)
      .eq("content_kind", data.contentKind as never)
      .eq("content_ref", data.contentRef)
      .limit(500);
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r) => r.id as string);
  });
